// The live data sync as one explicit state machine: where the catalog, the occupancy snapshot and the event stream
// stand, and what each event does next. web/live.mjs executes the effects; nothing else decides transitions.
//
//   booting    nothing yet
//   loading    full load: sites.bin and occupancy.bin in parallel, stream opened early, deltas buffered
//   live       catalog installed, events flowing
//   resyncing  catching up: reconnect with the last seq (the server replays), or refetch only occupancy.bin
//   hidden     tab in the background: no connection, data kept
//   offline    no network: data kept
//   error      a load or stream failed: waiting for the backoff timer
//   stale      the server has a new catalog version: data kept, no stream, until the tab returns or the user is idle
//   off        Live switched off in settings: catalog loaded, no stream
//
// Effects: load, reconnect, refresh, close, retry, arm-idle, ensure-stream. ctx: {loaded, stale, enabled, hidden,
// offline, returning}; the runner fills enabled/hidden/offline before each step, steps return a ctx patch.
const go = (state, effects = [], ctx = {}) => ({state, effects, ctx});

// Leaving hidden/offline/error/stale/off: the one place that decides where to continue.
export function resume(ctx) {
  if (!ctx.loaded) return go('loading', ['load']);
  if (!ctx.enabled) return go('off');
  if (ctx.stale) return go('loading', ['load']);
  return go('resyncing', ['reconnect'], {returning: true});
}
const wake = ctx => ctx.hidden ? go('hidden') : ctx.offline ? go('offline') : resume(ctx);
const to = (state, ...effects) => () => go(state, effects);
const park = (state = 'hidden') => () => go(state, ['close']);
const fail = () => go('error', ['close', 'retry']);
const stale = () => go('stale', ['close', 'arm-idle'], {stale: true});
const disable = () => go('off', ['close']);

export const TABLE = {
  booting: {START: wake},
  loading: {
    LOADED: ctx => ctx.enabled ? go('live', ['ensure-stream'], {loaded: true, stale: false, returning: false}) : go('off', ['close'], {loaded: true, stale: false}),
    FAIL: fail, CATALOG: fail, HIDE: park('hidden'), OFFLINE: park('offline'),
  },
  live: {GAP: to('resyncing', 'refresh'), RESYNC: to('resyncing', 'refresh'), CATALOG: stale, FAIL: fail,
    HIDE: park('hidden'), OFFLINE: park('offline'), DISABLE: disable},
  resyncing: {
    SYNCED: () => go('live', [], {returning: false}),
    GAP: to('resyncing', 'refresh'), RESYNC: to('resyncing', 'refresh'),
    // Returning to the tab is the moment to take a new catalog; in the middle of use it waits.
    CATALOG: ctx => ctx.returning ? go('loading', ['load'], {stale: true, returning: false}) : stale(),
    FAIL: fail, HIDE: park('hidden'), OFFLINE: park('offline'), DISABLE: disable,
  },
  hidden: {SHOW: wake},
  offline: {ONLINE: wake, SHOW: wake, HIDE: park('hidden'), DISABLE: disable, ENABLE: wake},
  error: {RETRY: wake, ONLINE: wake, HIDE: park('hidden'), OFFLINE: park('offline'), DISABLE: disable},
  stale: {IDLE: wake, SHOW: wake, ONLINE: wake, HIDE: park('hidden'), OFFLINE: park('offline'), DISABLE: disable},
  off: {ENABLE: wake, HIDE: park('hidden'), SHOW: ctx => ctx.loaded ? go('off') : wake(ctx)},
};
export const SYNC_STATES = Object.keys(TABLE);
export const SYNC_EVENTS = [...new Set(Object.values(TABLE).flatMap(row => Object.keys(row)))];

// Unlisted pairs are deliberate no-ops (a heartbeat-late FAIL in a parked state, a second HIDE, ...).
export function syncStep(state, event, ctx) {
  const rule = TABLE[state]?.[event];
  return rule ? rule(ctx) : go(state);
}
